import React, { useEffect, useState } from "react";
import { Video } from "../../types/cms";
import { iframeEmbedURL, parseVideoSource } from "../../lib/videoSources";
import { resolveXVideo } from "../../lib/xVideo";

/**
 * Plays a video from its hosted file, an X post (resolved to mp4 by our
 * Cloud Function) or a YouTube/Vimeo embed.
 */
export default function VideoPlayer({
  video,
  autoPlay = false,
  className = "w-full aspect-video bg-black",
}: {
  video: Pick<Video, "videoURL" | "videoFileURL" | "title" | "thumbnailURL">;
  autoPlay?: boolean;
  className?: string;
}) {
  const source = video.videoURL ? parseVideoSource(video.videoURL) : null;
  const [xVideo, setXVideo] = useState<{ mp4URL: string; posterURL?: string } | null>(null);
  const [xFailed, setXFailed] = useState(false);

  useEffect(() => {
    if (video.videoFileURL || source?.provider !== "x" || !source.id) return;
    let active = true;
    setXFailed(false);
    resolveXVideo(source.id).then((res) => {
      if (!active) return;
      if (res) setXVideo(res);
      else setXFailed(true);
    });
    return () => {
      active = false;
    };
  }, [video.videoFileURL, source?.provider, source?.id]);

  const fileURL = video.videoFileURL || (source?.provider === "file" ? source.url : undefined);
  if (!fileURL && xVideo) {
    // X refuses its mp4 files when a foreign Referer is sent (403), and
    // <video> can't drop the Referer: an iframe with no-referrer can.
    return (
      <iframe
        className={className}
        src={xVideo.mp4URL}
        title={video.title}
        referrerPolicy="no-referrer"
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
      />
    );
  }
  if (fileURL) {
    return (
      <video
        className={className}
        src={fileURL}
        poster={video.thumbnailURL}
        controls
        playsInline
        autoPlay={autoPlay}
        preload="metadata"
      >
        <track kind="captions" />
      </video>
    );
  }

  if (source?.provider === "x") {
    return (
      <div className={`${className} flex flex-col items-center justify-center gap-3 text-neutral-300`}>
        {xFailed ? (
          <>
            <p>This video can't be played here.</p>
            <a href={source.url} target="_blank" rel="noopener noreferrer" className="underline text-white">
              Watch it on X
            </a>
          </>
        ) : (
          <p className="animate-pulse">Loading video…</p>
        )}
      </div>
    );
  }

  const embed = video.videoURL ? iframeEmbedURL(video.videoURL) : null;
  if (!embed) {
    return (
      <div className={`${className} flex items-center justify-center text-neutral-400`}>
        No video available
      </div>
    );
  }
  const src = autoPlay ? `${embed}${embed.includes("?") ? "&" : "?"}autoplay=1` : embed;
  return (
    <iframe
      className={className}
      src={src}
      title={video.title}
      frameBorder={0}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
      allowFullScreen
      loading="lazy"
    />
  );
}
